import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, errorText } from '../api';
import { useAuth } from '../auth';
import { TicketNeuDialog } from '../components/Tickets';
import { EinsatzZeile } from '../components/Einsatz';
import { AufgabeKarte } from '../components/Vorgang';
import { Empty, Loading, Notice } from '../components/ui';
import type { Aufgabe, Kachel } from '../types';

interface Tag {
  kacheln: { art: Kachel; offen: number | null; ueberfaellig: number | null }[];
  ueberfaellig: Aufgabe[];
  heute: Aufgabe[];
  demnaechst: Aufgabe[];
  eskaliert: Aufgabe[];
  siehtEskalationen: boolean;
  einsaetzeHeute: {
    id: string;
    nummer: string;
    art: string;
    zeit_von: string | null;
    zeit_bis: string | null;
    strasse: string | null;
    plz: string | null;
    ort: string | null;
    status: string;
    titel: string | null;
    kunde_vorname: string | null;
    kunde_nachname: string | null;
    kunde_firma: string | null;
  }[];
}

export const KACHEL_INFO: Record<Kachel, { titel: string; symbol: string; text: string; pfad: string }> = {
  bestellung: { titel: 'Bestellung', symbol: '🛒', text: 'Ware für Kunden bestellen und nachverfolgen', pfad: '/bestellungen' },
  lieferung: { titel: 'Lieferung', symbol: '🚚', text: 'Liefertermine, Touren und Einsatzprotokolle', pfad: '/lieferungen' },
  service: { titel: 'Service', symbol: '🔧', text: 'Werkstatt und Außendienst', pfad: '/service' },
};

function Block({ titel, tickets, leer, zeigeZustaendig }: { titel: React.ReactNode; tickets: Aufgabe[]; leer?: string; zeigeZustaendig?: boolean }) {
  if (tickets.length === 0 && !leer) return null;
  return (
    <section className="fristblock">
      <h3>{titel}</h3>
      {tickets.length === 0 ? (
        <div className="small muted">{leer}</div>
      ) : (
        <div className="ticketliste">
          {tickets.map((t) => (
            <AufgabeKarte key={t.id} a={t} zeigeZustaendig={zeigeZustaendig} />
          ))}
        </div>
      )}
    </section>
  );
}

export function MeinTagPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tag, setTag] = useState<Tag | null>(null);
  const [fehler, setFehler] = useState('');
  const [neu, setNeu] = useState(false);

  const laden = useCallback(() => {
    api<Tag>('/api/mein-tag')
      .then(setTag)
      .catch((e) => setFehler(errorText(e)));
  }, []);
  useEffect(laden, [laden]);

  const vorname = user?.name.split(' ')[0] ?? '';
  const stunde = new Date().getHours();
  const gruss = stunde < 11 ? 'Guten Morgen' : stunde < 18 ? 'Hallo' : 'Guten Abend';

  return (
    <>
      <div className="kopf">
        <div>
          <h1>
            {gruss}, {vorname}
          </h1>
          <div className="untertitel">
            {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
        <span className="spacer" />
        <button className="primary" onClick={() => setNeu(true)}>
          + Neues Ticket
        </button>
      </div>

      {fehler && <Notice kind="error">{fehler}</Notice>}
      {!tag ? (
        <Loading />
      ) : (
        <>
          <div className="kacheln">
            {tag.kacheln.map((k) => (
              <Link key={k.art} to={KACHEL_INFO[k.art].pfad} className={`kachel ${k.art}`}>
                <span className="symbol">{KACHEL_INFO[k.art].symbol}</span>
                <span className="titel">{KACHEL_INFO[k.art].titel}</span>
                <span className="zahl">
                  {k.offen === null ? (
                    KACHEL_INFO[k.art].text
                  ) : (
                    <>
                      {k.offen} offen
                      {!!k.ueberfaellig && <span className="badge bad" style={{ marginLeft: 6 }}>{k.ueberfaellig} überfällig</span>}
                    </>
                  )}
                </span>
              </Link>
            ))}
          </div>

          {tag.einsaetzeHeute.length > 0 && (
            <section className="fristblock">
              <h3>
                Meine Einsätze heute <span className="badge info">{tag.einsaetzeHeute.length}</span>
              </h3>
              <div className="ticketliste">
                {tag.einsaetzeHeute.map((e) => (
                  <EinsatzZeile key={e.id} e={e} />
                ))}
              </div>
            </section>
          )}

          {tag.siehtEskalationen && tag.eskaliert.length > 0 && (
            <Block titel={<>Eskaliert – im Haus überfällig <span className="badge bad">{tag.eskaliert.length}</span></>} tickets={tag.eskaliert} zeigeZustaendig />
          )}
          <Block
            titel={<>Überfällig {tag.ueberfaellig.length > 0 && <span className="badge bad">{tag.ueberfaellig.length}</span>}</>}
            tickets={tag.ueberfaellig}
          />
          <Block titel="Heute fällig" tickets={tag.heute} leer={tag.ueberfaellig.length === 0 && tag.demnaechst.length > 0 ? 'Für heute ist nichts mehr offen.' : undefined} />
          <Block titel="Demnächst" tickets={tag.demnaechst} />
          {tag.ueberfaellig.length + tag.heute.length + tag.demnaechst.length === 0 && <Empty>Bei dir ist nichts offen. 👍</Empty>}
        </>
      )}

      {neu && (
        <TicketNeuDialog
          onClose={() => setNeu(false)}
          onAngelegt={(t) => {
            setNeu(false);
            navigate(`/tickets/${t.id}`);
          }}
        />
      )}
    </>
  );
}
